import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useProjectService, useWorkspaceService } from '@web/app/features/home/hooks';
import axios from 'axios';

import { useGetWorkspace } from '@codes/workspace';

import { useSelectedStore } from '../stores';

export enum PathError {
    InvalidWorkspace = 'invalidWorkspace',
    InvalidProject = 'invalidProject',
    NoAccessToWorkspace = 'noAccessToWorkspace',
    NoAccessToProject = 'noAccessToProject',
    WorkspaceNotFound = 'workspaceNotFound',
    ProjectNotFound = 'projectNotFound',
}

const toWorkspaceError = (e: unknown): PathError => {
    if (!axios.isAxiosError(e)) {
        return PathError.InvalidWorkspace;
    }

    if (e.response?.status === 404) {return PathError.WorkspaceNotFound;}
    if (e.response?.status === 403) {return PathError.NoAccessToWorkspace;}
    return PathError.InvalidWorkspace;
};

export const usePathGuard = () => {
    const [isChecking, setIsChecking] = useState(true);
    const [error, setError] = useState<PathError | null>(null);

    const { workspaceId, projectId } = useParams();

    const { mutateAsync: getWorkspace } = useGetWorkspace();

    const { setProject, setWorkspace } = useSelectedStore();
    const { myProject, myProjectsQueryResult, myProjects } = useProjectService();

    const { replaceWorkspace, workspaceFromServerQueryData, workspaceFromServer } = useWorkspaceService();

    useEffect(() => {
        const checkProjectId = (targetWorkspaceId: string) => {
            if (!projectId) {
                setIsChecking(false);
                return;
            }
            if (myProject?.id === projectId) {
                setIsChecking(false);
                return;
            }
            if (!myProjectsQueryResult.isSuccess) {
                return;
            }

            const project = myProjects.find(p => p.id === projectId);

            if (!project) {
                setError(PathError.ProjectNotFound);
                setIsChecking(false);
                return;
            }

            if (project.workspaceId !== targetWorkspaceId) {
                setError(PathError.NoAccessToProject);
            }
            setProject(project);
            setIsChecking(false);
        };

        const checkWorkspaceId = async () => {
            if (!workspaceId) {
                setIsChecking(false);
                return;
            }
            if (workspaceFromServerQueryData.isError) {
                setIsChecking(false);
                setProject(null);
                setWorkspace(null);
                return;
            }
            if (!workspaceFromServerQueryData.isSuccess) {
                return;
            }

            if (workspaceFromServer?.id !== workspaceId) {
                try {
                    const newWorkspace = await getWorkspace(workspaceId);
                    await replaceWorkspace(newWorkspace.id as string, newWorkspace.siteId as string);
                    checkProjectId(newWorkspace.id as string);
                } catch (e) {
                    setError(toWorkspaceError(e));
                    setIsChecking(false);
                }
            } else {
                checkProjectId(workspaceId);
            }
        };

        checkWorkspaceId();
    }, [
        workspaceId,
        workspaceFromServerQueryData.isSuccess,
        workspaceFromServerQueryData.isError,
        workspaceFromServer?.id,
        projectId,
        myProject?.id,
        myProjectsQueryResult.isSuccess,
    ]);

    return { isChecking, error };
};
